import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
// eslint-disable-next-line @typescript-eslint/no-require-imports
const XLSX = require('xlsx');
// eslint-disable-next-line @typescript-eslint/no-require-imports
const { saveAs } = require('file-saver');

type HistoryRow = {
  id: number;
  siteId: number | null;
  message: string;
  metadata?: {
    alarm_type_id?: number | string | null;
    sound?: number | string | null;
    site_name?: string | null;
    site_address?: string | null;
  };
  acknowledged?: boolean;
  acknowledgedAt?: string | null;
  acknowledgedByUserName?: string | null;
  comment?: string | null;
  createdAt?: string;
  siteName?: string | null;
  severity?: string;
  alertType?: string;
};

type EuSiteStat = { siteId: number; siteName: string; count: number };
type AlarmTypeStat = { alarmTypeKey: number; count: number };

interface ExportPayload {
  rows: HistoryRow[];
  byEuSite: EuSiteStat[];
  byAlarmType: AlarmTypeStat[];
  total: number;
  from: string;
  to: string;
  alarmTypeLabel: (key: number) => string;
  siteDisplayName: (row: HistoryRow) => string;
  rowTypeLabel: (row: HistoryRow) => string;
}

function fmtDate(iso?: string): string {
  if (!iso) return '—';
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return '—';
  return dt.toLocaleString('es-CO', { dateStyle: 'short', timeStyle: 'short' });
}

function rangeLabel(from: string, to: string): string {
  const parts: string[] = [];
  if (from) parts.push(`Desde: ${from}`);
  if (to) parts.push(`Hasta: ${to}`);
  return parts.length ? parts.join('  ·  ') : 'Sin filtro de fechas';
}

function statusLabel(ack?: boolean): string {
  return ack ? 'Gestionada' : 'Pendiente';
}

// ─── PDF ──────────────────────────────────────────────────────────────

export function exportToPdf(data: ExportPayload): void {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();

  const PRIMARY = [166, 27, 52] as const;   // #A61B34
  const DARK = [26, 26, 26] as const;       // #1a1a1a
  const LIGHT_BG = [245, 245, 245] as const;
  const WHITE = [255, 255, 255] as const;

  // Header bar
  doc.setFillColor(...PRIMARY);
  doc.rect(0, 0, pageW, 22, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(...WHITE);
  doc.text('Reporte de Alertas — Escudo Urbano', 14, 14);

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(`Generado: ${new Date().toLocaleString('es-CO')}`, pageW - 14, 14, { align: 'right' });

  // Range + total
  doc.setTextColor(80, 80, 80);
  doc.setFontSize(10);
  doc.text(rangeLabel(data.from, data.to), 14, 30);
  doc.text(`Total de registros: ${data.total}`, pageW - 14, 30, { align: 'right' });

  let startY = 36;

  // ─── Statistics section ─────────────────────────────────────
  const hasStats = data.byEuSite.length > 0 || data.byAlarmType.length > 0;

  if (hasStats) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(...DARK);
    doc.text('Estadísticas', 14, startY);
    startY += 4;

    const halfW = (pageW - 42) / 2;

    // Left table: by site
    if (data.byEuSite.length > 0) {
      autoTable(doc, {
        startY,
        margin: { left: 14 },
        tableWidth: halfW,
        head: [['Punto / Sitio', 'Alertas']],
        body: data.byEuSite.map((s) => [s.siteName || `Sitio #${s.siteId}`, String(s.count)]),
        headStyles: {
          fillColor: PRIMARY as [number, number, number],
          textColor: WHITE as [number, number, number],
          fontStyle: 'bold',
          fontSize: 8,
          halign: 'left',
        },
        bodyStyles: { fontSize: 8, textColor: [40, 40, 40] },
        alternateRowStyles: { fillColor: LIGHT_BG as [number, number, number] },
        columnStyles: { 1: { halign: 'right', cellWidth: 22 } },
        theme: 'grid',
        styles: { lineColor: [200, 200, 200], lineWidth: 0.2 },
      });
    }

    // Right table: by alarm type
    if (data.byAlarmType.length > 0) {
      autoTable(doc, {
        startY,
        margin: { left: 14 + halfW + 14 },
        tableWidth: halfW,
        head: [['Tipo de Alarma', 'Total']],
        body: data.byAlarmType.map((t) => [data.alarmTypeLabel(t.alarmTypeKey), String(t.count)]),
        headStyles: {
          fillColor: PRIMARY as [number, number, number],
          textColor: WHITE as [number, number, number],
          fontStyle: 'bold',
          fontSize: 8,
          halign: 'left',
        },
        bodyStyles: { fontSize: 8, textColor: [40, 40, 40] },
        alternateRowStyles: { fillColor: LIGHT_BG as [number, number, number] },
        columnStyles: { 1: { halign: 'right', cellWidth: 22 } },
        theme: 'grid',
        styles: { lineColor: [200, 200, 200], lineWidth: 0.2 },
      });
    }

    // Advance past whichever table is taller
    const lastY = (doc as any).lastAutoTable?.finalY ?? startY + 10;
    startY = lastY + 10;
  }

  // ─── Alerts table ───────────────────────────────────────────
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(...DARK);
  doc.text('Detalle de Alertas', 14, startY);
  startY += 4;

  const tableBody = data.rows.map((r) => [
    fmtDate(r.createdAt),
    data.siteDisplayName(r),
    data.rowTypeLabel(r),
    statusLabel(r.acknowledged),
    r.acknowledged && r.acknowledgedByUserName ? String(r.acknowledgedByUserName).trim() : '—',
    r.comment && String(r.comment).trim() ? String(r.comment).trim() : '—',
  ]);

  autoTable(doc, {
    startY,
    margin: { left: 14, right: 14 },
    head: [['Fecha', 'Sitio', 'Tipo EU', 'Estado', 'Gestionó', 'Comentario']],
    body: tableBody,
    headStyles: {
      fillColor: PRIMARY as [number, number, number],
      textColor: WHITE as [number, number, number],
      fontStyle: 'bold',
      fontSize: 8.5,
    },
    bodyStyles: { fontSize: 8, textColor: [40, 40, 40], cellPadding: 3 },
    alternateRowStyles: { fillColor: LIGHT_BG as [number, number, number] },
    columnStyles: {
      0: { cellWidth: 32 },
      3: { cellWidth: 24, halign: 'center' },
      5: { cellWidth: 55 },
    },
    theme: 'grid',
    styles: { lineColor: [200, 200, 200], lineWidth: 0.2, overflow: 'linebreak' },
    didParseCell(hookData: any) {
      if (hookData.section === 'body' && hookData.column.index === 3) {
        const val = String(hookData.cell.raw);
        if (val === 'Pendiente') {
          hookData.cell.styles.textColor = [180, 30, 50];
          hookData.cell.styles.fontStyle = 'bold';
        } else if (val === 'Gestionada') {
          hookData.cell.styles.textColor = [22, 120, 60];
          hookData.cell.styles.fontStyle = 'bold';
        }
      }
    },
  });

  // Footer on every page
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    const pageH = doc.internal.pageSize.getHeight();
    doc.setFontSize(7.5);
    doc.setTextColor(160, 160, 160);
    doc.text(`Página ${i} de ${pageCount}`, pageW - 14, pageH - 8, { align: 'right' });
    doc.text('VMS · Escudo Urbano', 14, pageH - 8);
  }

  doc.save(`alertas_eu_${new Date().toISOString().slice(0, 10)}.pdf`);
}

// ─── Excel ────────────────────────────────────────────────────

export function exportToExcel(data: ExportPayload): void {
  const wb = XLSX.utils.book_new();

  // Sheet 1: Alertas
  const alertHeaders = ['Fecha', 'Sitio', 'Tipo EU', 'Estado', 'Gestionó', 'Comentario'];
  const alertData = data.rows.map((r) => [
    fmtDate(r.createdAt),
    data.siteDisplayName(r),
    data.rowTypeLabel(r),
    statusLabel(r.acknowledged),
    r.acknowledged && r.acknowledgedByUserName ? String(r.acknowledgedByUserName).trim() : '—',
    r.comment && String(r.comment).trim() ? String(r.comment).trim() : '—',
  ]);
  const ws1 = XLSX.utils.aoa_to_sheet([
    [`Reporte de Alertas — Escudo Urbano`],
    [rangeLabel(data.from, data.to), '', '', `Total: ${data.total}`],
    [],
    alertHeaders,
    ...alertData,
  ]);
  ws1['!cols'] = [
    { wch: 18 },
    { wch: 28 },
    { wch: 26 },
    { wch: 14 },
    { wch: 20 },
    { wch: 45 },
  ];
  XLSX.utils.book_append_sheet(wb, ws1, 'Alertas');

  // Sheet 2: Stats by site
  if (data.byEuSite.length > 0) {
    const siteHeaders = ['Punto / Sitio', 'Alertas'];
    const siteData = data.byEuSite.map((s) => [s.siteName || `Sitio #${s.siteId}`, s.count]);
    const ws2 = XLSX.utils.aoa_to_sheet([
      ['Actuaciones por EU'],
      [],
      siteHeaders,
      ...siteData,
    ]);
    ws2['!cols'] = [{ wch: 30 }, { wch: 12 }];
    XLSX.utils.book_append_sheet(wb, ws2, 'Por Sitio');
  }

  // Sheet 3: Stats by alarm type
  if (data.byAlarmType.length > 0) {
    const typeHeaders = ['Tipo de Alarma', 'Total'];
    const typeData = data.byAlarmType.map((t) => [data.alarmTypeLabel(t.alarmTypeKey), t.count]);
    const ws3 = XLSX.utils.aoa_to_sheet([
      ['Top por Tipo de Alarma'],
      [],
      typeHeaders,
      ...typeData,
    ]);
    ws3['!cols'] = [{ wch: 30 }, { wch: 12 }];
    XLSX.utils.book_append_sheet(wb, ws3, 'Por Tipo');
  }

  const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  saveAs(blob, `alertas_eu_${new Date().toISOString().slice(0, 10)}.xlsx`);
}
